// The bottom sheet everything on this screen is entered in.
//
// Three fixed parts: a title that says what is being answered, a body that
// scrolls, and a footer pinned to the bottom so the primary action is always
// under a thumb. Only the body scrolls — a footer that scrolls away is how a
// driver loses the Save button on a long form.

import type { ReactNode } from 'react'

/**
 * How tall the sheet opens.
 *
 * Sizing to content made every sheet look like a scrap of paper at the bottom
 * of the screen. A form gets nearly the whole screen; a question that is two
 * taps gets only what it needs.
 */
export type SheetHeight = 'auto' | 'half' | 'tall' | 'full'

const HEIGHTS: Record<SheetHeight, string> = {
  auto: '',
  half: 'h-[55%]',
  tall: 'h-[80%]',
  full: 'h-[92%]',
}

export interface SheetShellProps {
  title?: string
  height?: SheetHeight
  /** The period or employer the title refers to. */
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  /** Dismissing by the veil or the grip. Omitted when the sheet must be answered. */
  onDismiss?: () => void
  labelledBy?: string
}

export const SheetShell = ({
  title,
  height = 'auto',
  subtitle,
  children,
  footer,
  onDismiss,
  labelledBy,
}: SheetShellProps) => (
  <div className="absolute inset-0 flex flex-col justify-end">
    <button
      type="button"
      aria-label="Dismiss"
      tabIndex={onDismiss ? 0 : -1}
      onClick={onDismiss}
      disabled={!onDismiss}
      className="absolute inset-0 bg-doubleNickel-gray-900/40"
    />
    <div
      role="dialog"
      aria-modal="true"
      aria-label={labelledBy ? undefined : title}
      aria-labelledby={labelledBy}
      className={`relative flex max-h-full flex-col overflow-hidden rounded-t-[22px] bg-doubleNickel-gray-50 ${HEIGHTS[height]}`}
    >
      <div className="mx-auto mb-0.5 mt-2 h-[4.5px] w-9 flex-none rounded-[3px] bg-doubleNickel-gray-300" />
      {title && (
        <div className="px-4 pb-0.5 pt-1 text-center text-[15px] font-semibold text-doubleNickel-gray-900">
          {title}
        </div>
      )}
      {subtitle && (
        <div className="px-4 pb-2 text-center text-[12px] text-doubleNickel-gray-500">
          {subtitle}
        </div>
      )}
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 pb-4 pt-1">
        {children}
      </div>
      {/*
        The safe-area inset below is what keeps Save clear of the browser's own
        toolbar. Without it the primary action of the whole sheet sat half under
        Safari's chrome.
      */}
      {footer && (
        <div className="flex-none border-t border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3">
          {footer}
        </div>
      )}
    </div>
  </div>
)
